import { userConfigPath, userProfilePath } from "../../functions/src/admin/paths";
import { getWalletHistorySchoolYearRange, getWalletHistorySchoolYearStartYear, getWalletHistorySchoolYearStartYearFromTimestamp } from "../../functions/src/api/dates";
import type { UserConfig, UserProfile } from "../../functions/src/types";
import { auth } from "./firebase";
import { useDatabaseVal, useFirebaseUser } from "./firebaseHooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type SchoolYearHistorySetup = {
    ready: boolean;
    schoolYearStartYear: number | null;
    setSchoolYearStartYear: (startYear: number) => void;
    schoolYearOptions: number[];
};

export function useRegistrationTimestamp(): number | undefined {
    const uid = auth.currentUser?.uid;
    const user = useFirebaseUser();
    const [profile, profileSnap] = useDatabaseVal<UserProfile>(uid ? userProfilePath(uid) : null);
    const [config, configSnap] = useDatabaseVal<UserConfig>(uid ? userConfigPath(uid) : null);

    return useMemo((): number | undefined => {
        if (!uid || profileSnap === undefined || configSnap === undefined) return undefined;
        if (!profile?.registrationDate && !config?.registration?.timestamp && user === undefined) return undefined;
        return (
            profile?.registrationDate
            || config?.registration?.timestamp
            || (user?.metadata?.creationTime ? new Date(user.metadata.creationTime).getTime() : undefined)
            || Date.now()
        );
    }, [uid, profile, config, profileSnap, configSnap, user]);
}

/** Registration → current year range; default = school year of most recent activity timestamp. */
export function useSchoolYearHistorySetup(lastActivityTimestamp: number | null | undefined): SchoolYearHistorySetup {
    const registrationTimestamp = useRegistrationTimestamp();
    const [schoolYearStartYear, setSchoolYearStartYear] = useState<number | null>(null);
    const userPickedYearRef = useRef(false);

    const { schoolYearOptions, defaultStartYear } = useMemo(() => {
        if (registrationTimestamp === undefined || lastActivityTimestamp === undefined) {
            return { schoolYearOptions: [] as number[], defaultStartYear: null as number | null };
        }
        const earliestStartYear = getWalletHistorySchoolYearStartYear(new Date(registrationTimestamp));
        const latestStartYear = getWalletHistorySchoolYearStartYear();
        const options = getWalletHistorySchoolYearRange(earliestStartYear, latestStartYear);
        const preferredStartYear = lastActivityTimestamp != null
            ? getWalletHistorySchoolYearStartYearFromTimestamp(lastActivityTimestamp)
            : latestStartYear;
        const defaultStartYear = Math.min(Math.max(preferredStartYear, earliestStartYear), latestStartYear);
        return { schoolYearOptions: options, defaultStartYear };
    }, [registrationTimestamp, lastActivityTimestamp]);

    useEffect(() => {
        if (userPickedYearRef.current || defaultStartYear == null) return;
        setSchoolYearStartYear(defaultStartYear);
    }, [defaultStartYear]);

    const setSchoolYearStartYearUser = useCallback((startYear: number) => {
        userPickedYearRef.current = true;
        setSchoolYearStartYear(startYear);
    }, []);

    return {
        ready: schoolYearStartYear != null && schoolYearOptions.length > 0,
        schoolYearStartYear,
        setSchoolYearStartYear: setSchoolYearStartYearUser,
        schoolYearOptions,
    };
}
